import { useCallback, useEffect, useState } from 'react';

import { createRoomPoll } from 'mastodon/actions/rooms';
import type { ApiRoomPollJSON } from 'mastodon/api_types/rooms';
import { useAppDispatch } from 'mastodon/store';

import {
  MAX_POLL_OPTION,
  MAX_POLL_OPTIONS,
  MAX_POLL_QUESTION,
  MIN_POLL_OPTIONS,
} from '../polls';

import { RoomPollCard } from './room_poll_card';

interface Props {
  roomId: string;
  poll: ApiRoomPollJSON | null;
  /** Host or co-host: may start, close and clear a poll. */
  canManage: boolean;
  /** The create form is open (owned by RoomChat, which renders the trigger). */
  formOpen: boolean;
  onCloseForm: () => void;
}

/**
 * The poll, docked at the top of the chat column above the message list.
 *
 * Ticket 29, third round. Two failures shaped this, and both are worth keeping
 * written down because the obvious fix for either one re-creates the other.
 *
 * 1. The poll originally lived on the jumbotron, a full-width row ABOVE the
 *    video stage. On a wide screen the room is a grid whose rows are
 *    `auto auto minmax(0,1fr) auto auto`: the jumbotron row is sized to its
 *    content and the stage row absorbs whatever is left. A poll grew that row
 *    from ~40px to ~250px and the video lost exactly that much. The stream was
 *    not pushed by the poll, it was starved by it.
 *
 * 2. Moving it into the chat as `position: absolute` fixed the starvation but
 *    produced `chathides.jpeg`: an opaque card pinned to the top of a
 *    SCROLLING list hides whatever is under it by construction, and messages
 *    scroll under it as they pass.
 *
 * So it now sits in the chat's own flow, between the header and the message
 * list, and reserves real space there — nothing is ever underneath it. That is
 * safe for the video because `.room-chat` carries `min-height: 0` in both the
 * stacked and the theater layout, which kills the content-driven minimum: its
 * outer box does not depend on its contents, so a child inside it takes height
 * from `.room-chat__messages` (`flex: 1 1 0`) and never from the stage.
 *
 * The wrapper is rendered unconditionally, per the ticket's requirement 4 —
 * every poll state (form open, live, voted, closed, cleared) leaves the room's
 * layout boxes untouched, which `tests/e2e/test-room-poll-no-reflow.mjs`
 * asserts to the pixel rather than to a tolerance.
 */
export const RoomPollPanel: React.FC<Props> = ({
  roomId,
  poll,
  canManage,
  formOpen,
  onCloseForm,
}) => {
  const dispatch = useAppDispatch();

  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(
    Array.from({ length: MIN_POLL_OPTIONS }, () => ''),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On a phone the chat is only about a third of the room's height, so a poll
  // above it would leave little room for the conversation. This shrinks it to
  // a single bar and back, handing the height straight to the message list.
  // It resizes only within the chat column, so it still cannot reflow the
  // stage.
  const [collapsed, setCollapsed] = useState(false);

  const pollId = poll?.id ?? null;

  // A new poll always arrives open — otherwise someone who collapsed the last
  // one would silently miss the next.
  useEffect(() => {
    setCollapsed(false);
  }, [pollId]);

  const handleCollapseToggle = useCallback(() => {
    setCollapsed((v) => !v);
  }, []);

  const handleQuestionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuestion(e.target.value);
    },
    [],
  );

  const handleOptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const index = Number(e.target.dataset.optionIndex);
      const { value } = e.target;
      setOptions((current) =>
        current.map((option, i) => (i === index ? value : option)),
      );
    },
    [],
  );

  // Capped at MAX_POLL_OPTIONS, which is the ticket's "max 6" — the button
  // disappears at the cap rather than letting people add a seventh field the
  // server will refuse.
  const handleAddOption = useCallback(() => {
    setOptions((current) =>
      current.length >= MAX_POLL_OPTIONS ? current : [...current, ''],
    );
  }, []);

  const handleRemoveOption = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const index = Number(e.currentTarget.dataset.optionIndex);
      setOptions((current) =>
        current.length <= MIN_POLL_OPTIONS
          ? current
          : current.filter((_, i) => i !== index),
      );
    },
    [],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (busy) return;

      const trimmed = options.map((o) => o.trim()).filter((o) => o !== '');
      if (trimmed.length < MIN_POLL_OPTIONS) {
        setError(`Give the poll at least ${MIN_POLL_OPTIONS} options.`);
        return;
      }

      setBusy(true);
      setError(null);
      dispatch(createRoomPoll(roomId, question.trim(), trimmed))
        .then(() => {
          setQuestion('');
          setOptions(Array.from({ length: MIN_POLL_OPTIONS }, () => ''));
          onCloseForm();
          return undefined;
        })
        .catch((err: unknown) => {
          const message = (err as { response?: { data?: { error?: string } } })
            .response?.data?.error;
          setError(message ?? 'Could not start that poll.');
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [busy, options, question, dispatch, roomId, onCloseForm],
  );

  const showForm = formOpen && !poll && canManage;
  const empty = !poll && !showForm;

  return (
    <div
      className={`room-chat__poll-dock${empty ? ' room-chat__poll-dock--empty' : ''}${collapsed ? ' room-chat__poll-dock--collapsed' : ''}`}
      data-testid='room-poll-dock'
      aria-hidden={empty}
    >
      {!empty && (
        <button
          type='button'
          className='room-chat__poll-collapse'
          onClick={handleCollapseToggle}
          aria-expanded={!collapsed}
          data-testid='room-poll-collapse'
          title={collapsed ? 'Show the poll' : 'Hide the poll'}
        >
          <span className='room-chat__poll-collapse__label'>📊 Poll</span>
          <span aria-hidden>{collapsed ? '▾' : '▴'}</span>
        </button>
      )}

      <div className='room-chat__poll-dock__body'>
        {poll && (
          <RoomPollCard roomId={roomId} poll={poll} canManage={canManage} />
        )}

        {showForm && (
          <form
            className='room-chat__poll-form'
            onSubmit={handleSubmit}
            data-testid='jumbotron-poll-form'
          >
            <input
              type='text'
              required
              maxLength={MAX_POLL_QUESTION}
              placeholder='Ask a question'
              aria-label='Poll question'
              value={question}
              onChange={handleQuestionChange}
            />

            {options.map((option, index) => (
              <span
                className='room-chat__poll-option'
                // The list is a fixed-length set of text boxes with no identity
                // of their own; the index IS the identity here.
                key={index}
              >
                <input
                  type='text'
                  maxLength={MAX_POLL_OPTION}
                  placeholder={`Option ${index + 1}`}
                  aria-label={`Poll option ${index + 1}`}
                  data-option-index={index}
                  value={option}
                  onChange={handleOptionChange}
                />
                {options.length > MIN_POLL_OPTIONS && (
                  <button
                    type='button'
                    className='room-chat__poll-remove'
                    data-option-index={index}
                    onClick={handleRemoveOption}
                    aria-label={`Remove option ${index + 1}`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}

            {options.length < MAX_POLL_OPTIONS && (
              <button
                type='button'
                className='room-chat__poll-add-option'
                onClick={handleAddOption}
                data-testid='poll-add-option'
              >
                + Add option
              </button>
            )}

            <button type='submit' disabled={busy}>
              {busy ? 'Starting…' : 'Start poll'}
            </button>

            {error && (
              <span className='room-chat__poll-error' role='alert'>
                {error}
              </span>
            )}
          </form>
        )}
      </div>
    </div>
  );
};
